import { emptyFilter } from "@estelle/domain";

import { INVENTORY_FIELDS } from "@/modules/inventory/fields";
import { collectAll } from "@/components/table/export";
import { warehouseSelection } from "@/modules/inventory/byWarehouse";
import { projectInventoryRow } from "@/modules/inventory/projections";
import { collectWarehouseExport } from "@/modules/inventory/warehouseExport";
import { StockLevels } from "@/modules/inventory/screens/StockLevels";
import { UpdateStock } from "@/modules/inventory/screens/UpdateStock";
import type { ModuleUi } from "@/modules/types";

/**
 * The Inventory module.
 *
 * Low stock is a filter on `levels` (#602 part B), not a section of its own: a
 * chip on Stock Levels and a shared saved view, both the `belowThreshold` field
 * the server computes. The old `/inventory/low-stock` address redirects there
 * (`routes/router.tsx`).
 *
 * Adjustments is not a section any more (#602 part D): Update stock writes through
 * the same stock-adjustment path, and the `stock_adjustments` table and its
 * history stay. `/inventory/adjustments` redirects to Update stock.
 */
export const inventoryModule: ModuleUi = {
  id: "inventory",
  sections: {
    levels: {
      fields: INVENTORY_FIELDS,
      screen: StockLevels,
      // One warehouse view (#602 part C): the Table / By warehouse switch is
      // gone, and an old ?view=warehouse link lands on this same view. Export is
      // the filtered stock levels, a row per variant per warehouse; with every
      // warehouse chosen, the side-by-side table instead (#461).
      exportRows: (data, query) =>
        warehouseSelection(query.filter ?? emptyFilter()).kind === "all"
          ? collectWarehouseExport(data, query)
          : collectAll(
              (page) => data.inventory.list(page),
              query,
              projectInventoryRow,
            ),
      commonFilters: [
        "warehouse",
        "belowThreshold",
        "outOfStock",
        "productType",
      ],
      count: async (data, query) =>
        (await data.inventory.list({ ...query, limit: 1 })).total,
    },
    // Load counts and corrections for one warehouse (#602): a form, not a list.
    "update-stock": { fields: [], screen: UpdateStock, padded: true },
  },
};
